import { useState } from "react";
import { Check, Copy, RefreshCw, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { ROLE_LABEL, ago } from "@/lib/perms";
import { ScopePicker } from "@/components/ScopePicker";

const sel = "h-9 rounded-md bg-inset border border-line text-xs text-slate-200 px-2";
const run = async (fn, msg, reload) => { try { await fn(); if (msg) toast.success(msg); reload(); } catch (e) { toast.error(formatApiError(e)); } };

const InviteForm = ({ team, worldId, pages, reload }) => {
  const [f, setF] = useState({ email: "", role: "editor", scope: { all: true } });
  const submit = (e) => {
    e.preventDefault();
    run(async () => {
      const { data } = await api.post(`/worlds/${worldId}/invites`, { ...f, origin_url: window.location.origin });
      if (!data.email_sent) toast.warning("Invite saved, but the email didn't send. Try Resend.");
      setF({ ...f, email: "" });
    }, "Invite sent", reload);
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-line p-4" data-testid="invite-form">
      <div className="flex gap-2">
        <Input type="email" required placeholder="coauthor@story.com" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="h-9" data-testid="invite-email-input" />
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={sel} data-testid="invite-role-select">
          {(team.my_role === "owner" ? ["admin", "editor", "viewer"] : ["editor", "viewer"]).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
        </select>
      </div>
      {f.role === "editor" && <ScopePicker value={f.scope} onChange={(scope) => setF({ ...f, scope })} pages={pages} />}
      <button className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent text-xs font-semibold px-4 py-2" data-testid="invite-send-btn"><Send className="w-3.5 h-3.5" /> Send invite</button>
    </form>
  );
};

const LinkSettings = ({ team, worldId, pages, reload }) => {
  const [l, setL] = useState(team.link);
  const save = (patch) => { const next = { ...l, ...patch }; setL(next); run(() => api.put(`/worlds/${worldId}/invite-link`, next), null, reload); };
  const url = l.token ? `${window.location.origin}/join/${l.token}` : "";
  return (
    <div className="space-y-3 rounded-xl border border-line p-4" data-testid="invite-link-settings">
      <div className="flex items-center justify-between">
        <div><p className="text-sm text-slate-200">Invite link</p><p className="text-[11px] text-slate-500">{l.approval ? `Anyone who opens it can ask to join as ${ROLE_LABEL[l.role]}. You approve each person.` : `Anyone signed in who opens it joins as ${ROLE_LABEL[l.role]}.`}</p></div>
        <Switch checked={l.enabled} onCheckedChange={(enabled) => save({ enabled })} data-testid="invite-link-switch" />
      </div>
      {l.enabled && (
        <>
          <select value={l.role} onChange={(e) => save({ role: e.target.value })} className={sel} data-testid="invite-link-role">
            <option value="viewer">Viewer</option><option value="editor">Editor</option>
          </select>
          <label className="flex items-center justify-between gap-3 text-xs text-slate-300">
            <span>Approve people before they join</span>
            <Switch checked={!!l.approval} onCheckedChange={(approval) => save({ approval })} data-testid="invite-link-approval-switch" />
          </label>
          {l.role === "editor" && <ScopePicker value={l.scope} onChange={(scope) => setL({ ...l, scope })} pages={pages} />}
          {l.role === "editor" && <button onClick={() => save({})} className="text-xs text-amber-300" data-testid="invite-link-save-scope">Save link scope</button>}
          <div className="flex gap-2">
            <Input readOnly value={url} className="h-9 font-code text-[11px]" data-testid="invite-link-url" />
            <button onClick={() => { navigator.clipboard?.writeText(url); toast.success("Link copied"); }} className="px-3 rounded-md bg-amber-500 text-onaccent" data-testid="invite-link-copy"><Copy className="w-4 h-4" /></button>
            <button onClick={() => save({ reset: true })} title="Reset link" className="px-3 rounded-md border border-line text-slate-300 hover:text-amber-300" data-testid="invite-link-reset"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </>
      )}
    </div>
  );
};

const RequestRow = ({ r, team, worldId, pages, reload }) => {
  const [role, setRole] = useState(team.link.role || "viewer");
  const [scope, setScope] = useState(team.link.scope?.all || team.link.scope?.categories?.length || team.link.scope?.page_ids?.length ? team.link.scope : { all: true });
  return (
    <li className="py-3 space-y-2" data-testid={`join-request-${r.id}`}>
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0"><p className="text-sm text-slate-200 truncate">{r.name}</p><p className="text-[11px] text-slate-500 truncate">{r.email} · asked {ago(r.created_at)}</p></div>
        <select value={role} onChange={(e) => setRole(e.target.value)} className={sel} data-testid={`join-request-role-${r.id}`}>
          {(team.my_role === "owner" ? ["admin", "editor", "viewer"] : ["editor", "viewer"]).map((x) => <option key={x} value={x}>{ROLE_LABEL[x]}</option>)}
        </select>
        <button onClick={() => run(() => api.post(`/join-requests/${r.id}/approve`, { role, scope }), `${r.name} joined`, reload)} title="Approve" className="p-1.5 rounded-md bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25" data-testid={`join-request-approve-${r.id}`}><Check className="w-4 h-4" /></button>
        <button onClick={() => run(() => api.post(`/join-requests/${r.id}/decline`), "Request declined", reload)} title="Decline" className="p-1.5 rounded-md text-slate-500 hover:text-rose-400" data-testid={`join-request-decline-${r.id}`}><X className="w-4 h-4" /></button>
      </div>
      {role === "editor" && <ScopePicker value={scope} onChange={setScope} pages={pages} />}
    </li>
  );
};

export const JoinRequests = (props) => props.team.requests?.length > 0 && (
  <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.03] p-4" data-testid="join-requests">
    <p className="text-sm text-amber-100">Waiting to join <span className="text-amber-400 font-code text-xs">{props.team.requests.length}</span></p>
    <ul className="divide-y divide-line">{props.team.requests.map((r) => <RequestRow key={r.id} r={r} {...props} />)}</ul>
  </div>
);

export const TeamInvites = (props) => (
  <div className="space-y-5" data-testid="team-invites">
    <JoinRequests {...props} />
    <InviteForm {...props} />
    {props.team.invites.length > 0 && (
      <ul className="divide-y divide-line" data-testid="pending-invites">
        {props.team.invites.map((i) => (
          <li key={i.id} className="py-3 flex items-center gap-3" data-testid={`invite-${i.id}`}>
            <div className="flex-1 min-w-0"><p className="text-sm text-slate-200 truncate">{i.email}</p><p className="text-[11px] text-slate-500">{ROLE_LABEL[i.role]} · sent {ago(i.created_at)}</p></div>
            <button onClick={() => run(() => api.post(`/invites/${i.id}/resend`), "Invite resent", props.reload)} className="text-xs text-slate-400 hover:text-amber-300" data-testid={`invite-resend-${i.id}`}>Resend</button>
            <button onClick={() => run(() => api.delete(`/invites/${i.id}`), "Invite cancelled", props.reload)} className="p-1 text-slate-500 hover:text-rose-400" data-testid={`invite-cancel-${i.id}`}><X className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    )}
    <LinkSettings key={props.team.link.token} {...props} />
  </div>
);
